/**
 * EffectsManager — manages Tone.js effect instances per track and master.
 *
 * Chains effects between track output and destination.
 * Supports add/remove/reorder/bypass/update operations.
 *
 * CompositeReverb uses an AudioWorklet crossfader for sample-accurate
 * equal-power wet/dry mixing. All contexts (timeline, export, preview)
 * use the same setMix/setMixAtTime/rampMixTo API — single source of truth.
 */
import * as Tone from 'tone';
import type { EffectInstance } from '../types';

type ToneEffect = Tone.Reverb | Tone.Freeverb | Tone.FeedbackDelay | Tone.Chorus | Tone.Distortion |
  Tone.Compressor | Tone.EQ3 | Tone.Phaser | Tone.Tremolo | Tone.BitCrusher | Tone.AutoFilter | Tone.Gain;

interface EffectNode {
  instance: EffectInstance;
  toneEffect: ToneEffect;
  bypass: Tone.Gain;
}

// Parameter metadata for proper slider rendering
export interface ParamMeta {
  label: string;
  min: number;
  max: number;
  step: number;
  unit?: string;
}

export interface EffectDef {
  label: string;
  category: string;
  defaults: Record<string, number>;
  params: Record<string, ParamMeta>;
}

// Available effect types with full parameter metadata
export const EFFECT_CATALOG: Record<string, EffectDef> = {
  // Dynamics
  compressor: {
    label: 'Compressor',
    category: 'Dynamics',
    defaults: { threshold: -24, ratio: 4, attack: 0.003, release: 0.25 },
    params: {
      threshold: { label: 'Threshold', min: -60, max: 0, step: 1, unit: 'dB' },
      ratio: { label: 'Ratio', min: 1, max: 20, step: 0.5 },
      attack: { label: 'Attack', min: 0, max: 1, step: 0.001, unit: 's' },
      release: { label: 'Release', min: 0, max: 1, step: 0.01, unit: 's' },
    },
  },
  // EQ
  eq3: {
    label: 'EQ3',
    category: 'EQ',
    defaults: { low: 0, mid: 0, high: 0 },
    params: {
      low: { label: 'Low', min: -24, max: 24, step: 0.5, unit: 'dB' },
      mid: { label: 'Mid', min: -24, max: 24, step: 0.5, unit: 'dB' },
      high: { label: 'High', min: -24, max: 24, step: 0.5, unit: 'dB' },
    },
  },
  // Reverb / Delay
  reverb: {
    label: 'Reverb',
    category: 'Reverb/Delay',
    defaults: {
      roomSize: 0.7, preDelay: 0.01, brightness: 50,
      modulation: 2, loGain: 0, hiGain: 0, wet: 0.5,
    },
    params: {
      roomSize: { label: 'Room Size', min: 0, max: 1, step: 0.01 },
      preDelay: { label: 'Pre-Delay', min: 0, max: 0.2, step: 0.001, unit: 's' },
      brightness: { label: 'Brightness', min: 6, max: 100, step: 1, unit: '%' },
      modulation: { label: 'Modulation', min: 0, max: 100, step: 1, unit: '%' },
      loGain: { label: 'Lo Gain', min: -18, max: 6, step: 0.5, unit: 'dB' },
      hiGain: { label: 'Hi Gain', min: -18, max: 6, step: 0.5, unit: 'dB' },
      wet: { label: 'Dry/Wet', min: 0, max: 1, step: 0.01 },
    },
  },
  delay: {
    label: 'Delay',
    category: 'Reverb/Delay',
    defaults: { delayTime: 0.25, feedback: 0.3, wet: 0.3 },
    params: {
      delayTime: { label: 'Time', min: 0.01, max: 1, step: 0.01, unit: 's' },
      feedback: { label: 'Feedback', min: 0, max: 0.95, step: 0.01 },
      wet: { label: 'Mix', min: 0, max: 1, step: 0.01 },
    },
  },
  // Modulation
  chorus: {
    label: 'Chorus',
    category: 'Modulation',
    defaults: { frequency: 1.5, delayTime: 3.5, depth: 0.7, wet: 0.3 },
    params: {
      frequency: { label: 'Rate', min: 0.1, max: 10, step: 0.1, unit: 'Hz' },
      delayTime: { label: 'Delay', min: 0.5, max: 20, step: 0.5, unit: 'ms' },
      depth: { label: 'Depth', min: 0, max: 1, step: 0.01 },
      wet: { label: 'Mix', min: 0, max: 1, step: 0.01 },
    },
  },
  phaser: {
    label: 'Phaser',
    category: 'Modulation',
    defaults: { frequency: 0.5, octaves: 3, baseFrequency: 350, wet: 0.3 },
    params: {
      frequency: { label: 'Rate', min: 0.1, max: 10, step: 0.1, unit: 'Hz' },
      octaves: { label: 'Octaves', min: 0.5, max: 6, step: 0.5 },
      baseFrequency: { label: 'Base Freq', min: 50, max: 2000, step: 10, unit: 'Hz' },
      wet: { label: 'Mix', min: 0, max: 1, step: 0.01 },
    },
  },
  tremolo: {
    label: 'Tremolo',
    category: 'Modulation',
    defaults: { frequency: 10, depth: 0.5, wet: 0.5 },
    params: {
      frequency: { label: 'Rate', min: 0.1, max: 20, step: 0.1, unit: 'Hz' },
      depth: { label: 'Depth', min: 0, max: 1, step: 0.01 },
      wet: { label: 'Mix', min: 0, max: 1, step: 0.01 },
    },
  },
  autoFilter: {
    label: 'Auto Filter',
    category: 'Modulation',
    defaults: { frequency: 1, baseFrequency: 200, octaves: 2.6, wet: 0.5 },
    params: {
      frequency: { label: 'Rate', min: 0.1, max: 10, step: 0.1, unit: 'Hz' },
      baseFrequency: { label: 'Base Freq', min: 50, max: 2000, step: 10, unit: 'Hz' },
      octaves: { label: 'Octaves', min: 0.5, max: 6, step: 0.1 },
      wet: { label: 'Mix', min: 0, max: 1, step: 0.01 },
    },
  },
  // Distortion
  distortion: {
    label: 'Distortion',
    category: 'Distortion',
    defaults: { distortion: 0.4, wet: 0.5 },
    params: {
      distortion: { label: 'Drive', min: 0, max: 1, step: 0.01 },
      wet: { label: 'Mix', min: 0, max: 1, step: 0.01 },
    },
  },
  bitCrusher: {
    label: 'Bit Crusher',
    category: 'Distortion',
    defaults: { bits: 8, wet: 0.5 },
    params: {
      bits: { label: 'Bits', min: 1, max: 16, step: 1 },
      wet: { label: 'Mix', min: 0, max: 1, step: 0.01 },
    },
  },
};

export type EffectType = keyof typeof EFFECT_CATALOG;

// ============================================================
// Crossfader Worklet Registration
// ============================================================

const registeredContexts = new WeakSet<BaseAudioContext>();

/** Register the crossfader AudioWorklet in the current Tone context (idempotent) */
export async function ensureCrossfaderWorklet(): Promise<boolean> {
  try {
    const ctx = Tone.getContext().rawContext as BaseAudioContext;
    if (registeredContexts.has(ctx)) return true;
    await (ctx as AudioContext).audioWorklet.addModule('/worklets/crossfader-processor.js');
    registeredContexts.add(ctx);
    return true;
  } catch {
    // Worklet not available — gain-node fallback will be used
    return false;
  }
}

/**
 * Whether to use the AudioWorklet crossfader.
 * Currently disabled — the gain-node fallback provides identical behavior
 * with the unified setMix API. Enable once worklet ↔ Tone.js multi-input
 * wiring is battle-tested.
 */
const USE_WORKLET = false;

// ============================================================
// CompositeReverb — Single Source of Truth via AudioWorklet
// ============================================================

/**
 * CompositeReverb — DaVinci Resolve-style reverb engine.
 *
 * Signal chain (with worklet):
 *   Input ─┬─ (dry) ──────────────────────────────────────── Crossfader input 0
 *          └─ PreDelay → Freeverb → EQ → Chorus ──────────── Crossfader input 1
 *                                                                    │
 *                                                              Crossfader output → _output → downstream
 *
 * Fallback (no worklet):
 *   Input ─┬─ _dryGain ──────────────────────────────────────┬─ _output → downstream
 *          └─ PreDelay → Freeverb → EQ → Chorus → _wetGain ──┘
 *
 * All contexts use the unified mix API:
 *   setMix(value)                    — immediate (timeline real-time, preview)
 *   setMixAtTime(value, time)        — scheduled (export)
 *   rampMixTo(value, endTime)        — linear ramp (export, timeline activation)
 */
class CompositeReverb extends Tone.Gain {
  private _preDelay: Tone.Delay;
  private _freeverb: Tone.Freeverb;
  private _toneEQ: Tone.EQ3;
  private _modChorus: Tone.Chorus;
  private _output: Tone.Gain;           // bridge for downstream Tone.js connections

  // Worklet mode
  private _crossfader: AudioWorkletNode | null = null;
  private _mixParam: AudioParam | null = null;

  // Fallback mode (gain-node crossfade)
  private _wetGain: Tone.Gain | null = null;
  private _dryGain: Tone.Gain | null = null;

  private _useWorklet: boolean;

  constructor(params: Record<string, number>, useWorklet: boolean) {
    super(1);

    this._useWorklet = useWorklet;
    const mixVal = params.wet ?? 0.5;

    // Pre-delay
    this._preDelay = new Tone.Delay(Math.max(0, params.preDelay ?? 0.01));

    // Core reverb — Freeverb (Schroeder/Moorer)
    const brightness = params.brightness ?? 50;
    const dampening = 500 + ((brightness - 6) / 94) * 15500;
    this._freeverb = new Tone.Freeverb({
      roomSize: params.roomSize ?? 0.7,
      dampening,
    });
    this._freeverb.wet.value = 1; // fully wet internally; crossfader handles mix

    // Tone shaping — EQ on the reverb tail
    this._toneEQ = new Tone.EQ3({
      low: params.loGain ?? 0,
      mid: 0,
      high: params.hiGain ?? 0,
      lowFrequency: 300,
      highFrequency: 4500,
    });

    // Modulation — chorus on tail to prevent metallic ringing
    const modDepth = (params.modulation ?? 2) / 100;
    this._modChorus = new Tone.Chorus({
      frequency: 0.8,
      delayTime: 3.5,
      depth: modDepth,
      wet: modDepth > 0 ? 1 : 0,
    });
    this._modChorus.start();

    // Output bridge — downstream Tone nodes connect to this
    this._output = new Tone.Gain(1);

    // Wire the reverb processing chain
    super.connect(this._preDelay);
    this._preDelay.connect(this._freeverb);
    this._freeverb.connect(this._toneEQ);
    this._toneEQ.connect(this._modChorus);

    if (useWorklet) {
      // ── Worklet mode ──
      // Crossfader: input 0 = dry, input 1 = wet, output = mixed
      // Use Tone.Gain bridges so Tone.js graph wiring works cleanly
      const ctx = Tone.getContext().rawContext as BaseAudioContext;
      this._crossfader = new AudioWorkletNode(ctx, 'crossfader-processor', {
        numberOfInputs: 2,
        numberOfOutputs: 1,
        outputChannelCount: [2],
      });
      this._mixParam = this._crossfader.parameters.get('mix')!;
      this._mixParam.value = mixVal;

      // Dry bridge: taps the input signal for the dry path
      const dryTap = new Tone.Gain(1);
      super.connect(dryTap);
      // Wet bridge: takes the chorus output
      const wetTap = new Tone.Gain(1);
      this._modChorus.connect(wetTap);
      // Out bridge: receives crossfader output
      const outTap = new Tone.Gain(1);
      outTap.connect(this._output);

      // Connect bridges to crossfader via raw Web Audio (multi-input)
      // Tone.Gain's underlying GainNode is accessible via .output on the internal chain
      // We use Tone.connect to get into raw node, then connect to worklet inputs
      Tone.connect(dryTap, this._crossfader, 0, 0);
      Tone.connect(wetTap, this._crossfader, 0, 1);
      this._crossfader.connect((outTap as unknown as { input: AudioNode }).input);
    } else {
      // ── Gain-node mode ── (equal-power via gain nodes)
      const dryVal = Math.cos(mixVal * Math.PI * 0.5);
      const wetVal = Math.sin(mixVal * Math.PI * 0.5);
      this._wetGain = new Tone.Gain(wetVal);
      this._dryGain = new Tone.Gain(dryVal);

      // Wet: chorus → wetGain → output
      this._modChorus.connect(this._wetGain);
      this._wetGain.connect(this._output);
      // Dry: input → dryGain → output
      super.connect(this._dryGain);
      this._dryGain.connect(this._output);
    }
  }

  // ── Unified Mix API (single source of truth) ──

  /** Set mix immediately (0 = dry, 1 = wet). Used by timeline & preview. */
  setMix(value: number): void {
    if (this._useWorklet && this._mixParam) {
      this._mixParam.value = value;
    } else if (this._wetGain && this._dryGain) {
      this._wetGain.gain.value = Math.sin(value * Math.PI * 0.5);
      this._dryGain.gain.value = Math.cos(value * Math.PI * 0.5);
    }
  }

  /** Ramp mix to value over duration (seconds). Used by timeline activation. */
  rampMixTo(value: number, duration: number): void {
    const now = Tone.now();
    if (this._useWorklet && this._mixParam) {
      this._mixParam.linearRampToValueAtTime(value, now + duration);
    } else if (this._wetGain && this._dryGain) {
      this._wetGain.gain.linearRampToValueAtTime(Math.sin(value * Math.PI * 0.5), now + duration);
      this._dryGain.gain.linearRampToValueAtTime(Math.cos(value * Math.PI * 0.5), now + duration);
    }
  }

  /** Schedule mix at a specific time. Used by offline export. */
  setMixAtTime(value: number, time: number): void {
    if (this._useWorklet && this._mixParam) {
      this._mixParam.setValueAtTime(value, time);
    } else if (this._wetGain && this._dryGain) {
      this._wetGain.gain.setValueAtTime(Math.sin(value * Math.PI * 0.5), time);
      this._dryGain.gain.setValueAtTime(Math.cos(value * Math.PI * 0.5), time);
    }
  }

  /** Schedule linear ramp to value at a specific time. Used by offline export. */
  rampMixToTime(value: number, endTime: number): void {
    if (this._useWorklet && this._mixParam) {
      this._mixParam.linearRampToValueAtTime(value, endTime);
    } else if (this._wetGain && this._dryGain) {
      this._wetGain.gain.linearRampToValueAtTime(Math.sin(value * Math.PI * 0.5), endTime);
      this._dryGain.gain.linearRampToValueAtTime(Math.cos(value * Math.PI * 0.5), endTime);
    }
  }

  // ── Reverb parameter accessors ──

  get roomSize() { return this._freeverb.roomSize; }
  get dampening() { return this._freeverb.dampening; }
  get preDelay() { return this._preDelay.delayTime; }
  get loGain() { return this._toneEQ.low; }
  get hiGain() { return this._toneEQ.high; }

  set brightness(pct: number) {
    const hz = 500 + ((Math.max(6, Math.min(100, pct)) - 6) / 94) * 15500;
    (this._freeverb.dampening as unknown as { value: number }).value = hz;
  }

  set modulation(pct: number) {
    const depth = Math.max(0, Math.min(100, pct)) / 100;
    this._modChorus.depth = depth;
    this._modChorus.wet.value = depth > 0 ? 1 : 0;
  }

  // ── Tone.js graph wiring ──

  connect(destination: Tone.InputNode, outputNum?: number, inputNum?: number): this {
    if (destination === this._preDelay || destination === this._dryGain) {
      return super.connect(destination, outputNum, inputNum);
    }
    this._output.connect(destination as Tone.InputNode, outputNum, inputNum);
    return this;
  }

  disconnect(destination?: Tone.InputNode, outputNum?: number, inputNum?: number): this {
    if (destination) {
      this._output.disconnect(destination, outputNum, inputNum);
    } else {
      this._output.disconnect();
    }
    return this;
  }

  dispose(): this {
    super.dispose();
    this._preDelay.dispose();
    this._freeverb.dispose();
    this._toneEQ.dispose();
    this._modChorus.dispose();
    if (this._crossfader) {
      this._crossfader.disconnect();
      this._crossfader = null;
      this._mixParam = null;
    }
    this._wetGain?.dispose();
    this._dryGain?.dispose();
    this._output.dispose();
    return this;
  }
}

// ============================================================
// Type guard for the unified mix API
// ============================================================

/** Check if a ToneEffect has the CompositeReverb mix API */
export function hasMixAPI(effect: ToneEffect): effect is ToneEffect & {
  setMix: (v: number) => void;
  rampMixTo: (v: number, d: number) => void;
  setMixAtTime: (v: number, t: number) => void;
  rampMixToTime: (v: number, t: number) => void;
} {
  return 'setMix' in effect;
}

// ============================================================
// Factory
// ============================================================

export async function createToneEffect(type: string, params: Record<string, number>): Promise<ToneEffect> {
  switch (type) {
    case 'compressor': return new Tone.Compressor(params.threshold, params.ratio);
    case 'eq3': return new Tone.EQ3(params.low, params.mid, params.high);
    case 'reverb': {
      const hasWorklet = USE_WORKLET && await ensureCrossfaderWorklet();
      return new CompositeReverb(params, hasWorklet) as unknown as ToneEffect;
    }
    case 'delay': { const d = new Tone.FeedbackDelay(params.delayTime, params.feedback); d.wet.value = params.wet ?? 0.3; return d; }
    case 'chorus': { const c = new Tone.Chorus(params.frequency, params.delayTime, params.depth); c.wet.value = params.wet ?? 0.3; return c.start(); }
    case 'phaser': return new Tone.Phaser({ frequency: params.frequency, octaves: params.octaves, baseFrequency: params.baseFrequency });
    case 'tremolo': { const t = new Tone.Tremolo(params.frequency, params.depth); t.wet.value = params.wet ?? 0.5; return t.start(); }
    case 'autoFilter': { const a = new Tone.AutoFilter(params.frequency); a.wet.value = params.wet ?? 0.5; return a.start(); }
    case 'distortion': { const d = new Tone.Distortion(params.distortion); d.wet.value = params.wet ?? 0.5; return d; }
    case 'bitCrusher': { const b = new Tone.BitCrusher(params.bits); b.wet.value = params.wet ?? 0.5; return b; }
    default: return new Tone.Gain(1) as unknown as ToneEffect;
  }
}

// ============================================================
// EffectsManager
// ============================================================

export class EffectsManager {
  private chains = new Map<string, EffectNode[]>(); // trackId | 'master' → effects
  private input = new Map<string, Tone.ToneAudioNode>();
  private output = new Map<string, Tone.ToneAudioNode>();

  async addEffect(
    chainId: string,
    instance: EffectInstance,
    inputNode: Tone.ToneAudioNode,
    outputNode: Tone.ToneAudioNode,
  ): Promise<void> {
    const chain = this.chains.get(chainId) ?? [];
    const toneEffect = await createToneEffect(instance.type, instance.params);
    const bypass = new Tone.Gain(instance.bypassed ? 0 : 1);

    const node: EffectNode = { instance, toneEffect, bypass };
    chain.push(node);
    this.chains.set(chainId, chain);
    this.input.set(chainId, inputNode);
    this.output.set(chainId, outputNode);

    this.reconnectChain(chainId);
  }

  removeEffect(chainId: string, effectId: string): void {
    const chain = this.chains.get(chainId);
    if (!chain) return;

    const idx = chain.findIndex((n) => n.instance.id === effectId);
    if (idx === -1) return;

    chain[idx].toneEffect.dispose();
    chain[idx].bypass.dispose();
    chain.splice(idx, 1);

    this.reconnectChain(chainId);
  }

  updateParam(chainId: string, effectId: string, params: Record<string, number>): void {
    const chain = this.chains.get(chainId);
    if (!chain) return;

    const node = chain.find((n) => n.instance.id === effectId);
    if (!node) return;

    const effect = node.toneEffect;
    for (const [key, value] of Object.entries(params)) {
      // CompositeReverb custom setters
      if (effect instanceof CompositeReverb) {
        if (key === 'brightness') { effect.brightness = value; continue; }
        if (key === 'modulation') { effect.modulation = value; continue; }
      }
      // Unified mix API — single source of truth
      if (key === 'wet' && hasMixAPI(effect)) {
        effect.setMix(value);
      } else if (key === 'wet' && 'wet' in effect) {
        (effect as { wet: Tone.Signal<'normalRange'> }).wet.value = value;
      } else if (key in effect) {
        try {
          const prop = (effect as unknown as Record<string, unknown>)[key];
          if (prop && typeof prop === 'object' && 'value' in prop) {
            (prop as { value: number }).value = value;
          }
        } catch { /* some params not settable */ }
      }
    }
  }

  bypass(chainId: string, effectId: string, bypassed: boolean): void {
    const chain = this.chains.get(chainId);
    if (!chain) return;

    const node = chain.find((n) => n.instance.id === effectId);
    if (!node) return;

    node.instance.bypassed = bypassed;
    node.bypass.gain.value = bypassed ? 0 : 1;
    this.reconnectChain(chainId);
  }

  private reconnectChain(chainId: string): void {
    const chain = this.chains.get(chainId) ?? [];
    const input = this.input.get(chainId);
    const output = this.output.get(chainId);
    if (!input || !output) return;

    try { input.disconnect(); } catch { /* ok */ }
    for (const node of chain) {
      try { node.toneEffect.disconnect(); } catch { /* ok */ }
    }

    const activeEffects = chain.filter((n) => !n.instance.bypassed);
    if (activeEffects.length === 0) {
      input.connect(output);
      return;
    }

    input.connect(activeEffects[0].toneEffect);
    for (let i = 0; i < activeEffects.length - 1; i++) {
      activeEffects[i].toneEffect.connect(activeEffects[i + 1].toneEffect);
    }
    activeEffects[activeEffects.length - 1].toneEffect.connect(output);
  }

  clearChain(chainId: string): void {
    const chain = this.chains.get(chainId);
    if (!chain) return;
    for (const node of chain) {
      try { node.toneEffect.disconnect(); } catch { /* ok */ }
      node.toneEffect.dispose();
      node.bypass.dispose();
    }
    this.chains.delete(chainId);
    this.input.delete(chainId);
    this.output.delete(chainId);
  }

  dispose(): void {
    for (const chain of this.chains.values()) {
      for (const node of chain) {
        node.toneEffect.dispose();
        node.bypass.dispose();
      }
    }
    this.chains.clear();
    this.input.clear();
    this.output.clear();
  }
}
